import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  HStack,
  IconButton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useState } from 'react';

import { EDigestPreference } from '@/graphql-types/globalTypes';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';
import { MoreHorizontalIcon } from '@/imports/ui/chakra/feather';
import { UserDigestSettingsModal } from '@/imports/ui/components/UserDigestSettingsModal';

import {
  OrganizationFollowButton_CreateMutation,
  OrganizationFollowButton_CreateMutationVariables,
} from './graphql-types/OrganizationFollowButton_CreateMutation';
import { OrganizationFollowButton_CurrentUser } from './graphql-types/OrganizationFollowButton_CurrentUser';
import {
  OrganizationFollowButton_DeleteMutation,
  OrganizationFollowButton_DeleteMutationVariables,
} from './graphql-types/OrganizationFollowButton_DeleteMutation';
import {
  OrganizationFollowButton_OrganizationQuery,
  OrganizationFollowButton_OrganizationQueryVariables,
} from './graphql-types/OrganizationFollowButton_OrganizationQuery';
import {
  OrganizationFollowButton_UpdateMutation,
  OrganizationFollowButton_UpdateMutationVariables,
} from './graphql-types/OrganizationFollowButton_UpdateMutation';

import { FollowButtonModal } from './FollowButtonModal';

const CURRENT_USER_QUERY = gql`
  query OrganizationFollowButton_CurrentUser {
    currentUser {
      _id
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation OrganizationFollowButton_CreateMutation(
    $input: CreateUserOrganizationLinkInput!
  ) {
    createUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          numFollowers
        }
      }
    }
  }
`;

const DELETE_MUTATION = gql`
  mutation OrganizationFollowButton_DeleteMutation(
    $input: DeleteUserOrganizationLinkInput!
  ) {
    deleteUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          numFollowers
        }
      }
    }
  }
`;

const ORGANIZATION_QUERY = gql`
  query OrganizationFollowButton_OrganizationQuery(
    $input: SingleOrganizationInput!
  ) {
    organization(input: $input) {
      result {
        _id
        displayName
        defaultDigestPref
        userOrganizationLink {
          _id
          digestPreference
        }
      }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation OrganizationFollowButton_UpdateMutation(
    $input: UpdateUserOrganizationLinkInput!
  ) {
    updateUserOrganizationLink(input: $input) {
      data {
        _id
        digestPreference
      }
    }
  }
`;

type OrganizationFollowButtonProps = {
  organizationId: string;
};

export const OrganizationFollowButton: React.FC<
  OrganizationFollowButtonProps
> = (props) => {
  const analyticsClient = useAnalyticsLogger();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const currentUserQuery =
    useQuery<OrganizationFollowButton_CurrentUser>(CURRENT_USER_QUERY);

  const organizationQuery = useQuery<
    OrganizationFollowButton_OrganizationQuery,
    OrganizationFollowButton_OrganizationQueryVariables
  >(ORGANIZATION_QUERY, {
    variables: {
      input: {
        id: props.organizationId,
      },
    },
  });

  const [createUserOrganizationLink, createUserOrganizationLinkResult] =
    useMutation<
      OrganizationFollowButton_CreateMutation,
      OrganizationFollowButton_CreateMutationVariables
    >(CREATE_MUTATION, {
      refetchQueries: ['OrganizationFollowButton_OrganizationQuery'],
      awaitRefetchQueries: true,
    });

  const [deleteUserOrganizationLink, deleteUserOrganizationLinkResult] =
    useMutation<
      OrganizationFollowButton_DeleteMutation,
      OrganizationFollowButton_DeleteMutationVariables
    >(DELETE_MUTATION, {
      refetchQueries: ['OrganizationFollowButton_OrganizationQuery'],
      awaitRefetchQueries: true,
    });

  const [updateUserOrganizationLink, updateUserOrganizationLinkResult] =
    useMutation<
      OrganizationFollowButton_UpdateMutation,
      OrganizationFollowButton_UpdateMutationVariables
    >(UPDATE_MUTATION, {
      refetchQueries: ['OrganizationFollowButton_OrganizationQuery'],
      awaitRefetchQueries: true,
    });

  const signUpPrompt = useSignUpPrompt();

  if (currentUserQuery.error) {
    logError(currentUserQuery.error);
  }

  if (organizationQuery.error) {
    logError(organizationQuery.error);
  }

  const followOrganization = async (isConsented: boolean): Promise<void> => {
    if (!currentUserQuery.data?.currentUser) {
      signUpPrompt.showPrompt();
      return;
    }

    try {
      await createUserOrganizationLink({
        variables: {
          input: {
            data: {
              userId: currentUserQuery.data.currentUser._id,
              organizationId: props.organizationId,
              contactConsented: isConsented,
            },
          },
        },
      });
      analyticsClient.logUserFollowedSponsor();
      toast({
        description: `Successfully followed ${organizationQuery.data?.organization?.result?.displayName}.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        description: `Failed to follow ${organizationQuery.data?.organization?.result?.displayName}.`,
        status: 'error',
      });
      logError(err);
    }
  };

  const unfollowOrganization = async (): Promise<void> => {
    const userOrgLinkId =
      organizationQuery.data?.organization?.result?.userOrganizationLink?._id;

    if (!currentUserQuery.data?.currentUser || !userOrgLinkId) {
      return;
    }

    try {
      await deleteUserOrganizationLink({
        variables: {
          input: {
            id: userOrgLinkId,
          },
        },
      });
      analyticsClient.logUserUnfollowedSponsor();
      toast({
        description: `Successfully unfollowed ${organizationQuery.data?.organization?.result?.displayName}`,
        status: 'success',
      });
    } catch (err) {
      toast({
        description: `Failed to unfollow ${organizationQuery.data?.organization?.result?.displayName}`,
        status: 'error',
      });
      logError(err);
    }
  };

  const updateDigestPreference = async (
    pref: EDigestPreference
  ): Promise<void> => {
    try {
      await updateUserOrganizationLink({
        variables: {
          input: {
            id: organizationQuery.data?.organization?.result
              ?.userOrganizationLink?._id,
            data: {
              digestPreference: pref,
            },
          },
        },
      });
      toast({
        description: 'Successfully updated digest preference.',
        status: 'success',
      });
    } catch (err) {
      toast({
        description: 'Failed to update digest preference.',
        status: 'error',
      });
      logError(err);
    }
  };

  const isDisabled =
    currentUserQuery.loading ||
    signUpPrompt.loading ||
    organizationQuery.loading;

  const initialDigestPreference = (organizationQuery.data?.organization?.result
    ?.userOrganizationLink?.digestPreference ||
    organizationQuery.data?.organization?.result?.defaultDigestPref ||
    EDigestPreference.DAILY) as EDigestPreference;

  const isFollowing =
    !!organizationQuery.data?.organization?.result?.userOrganizationLink;

  const handleClick = () => {
    if (isFollowing) {
      unfollowOrganization();
    } else {
      setIsModalOpen(true);
    }
  };

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const onModalSubmit = (isConsented: boolean) => {
    followOrganization(isConsented);
    setIsModalOpen(false);
  };

  return (
    <HStack>
      {signUpPrompt.promptModal}

      <Button
        isLoading={
          createUserOrganizationLinkResult.loading ||
          deleteUserOrganizationLinkResult.loading
        }
        isDisabled={isDisabled}
        variant="outline"
        colorScheme="gray"
        onClick={handleClick}
      >
        {isFollowing ? 'Following' : 'Follow'}
      </Button>
      <FollowButtonModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        organizationName={
          organizationQuery.data?.organization?.result?.displayName ?? ''
        }
        onSubmit={onModalSubmit}
      />

      {isFollowing && (
        <IconButton
          aria-label="Digest Preferences"
          icon={<MoreHorizontalIcon />}
          variant="outline"
          isDisabled={isDisabled}
          onClick={onOpen}
          colorScheme="gray"
        ></IconButton>
      )}
      <UserDigestSettingsModal
        value={initialDigestPreference}
        isOpen={isOpen}
        onClose={onClose}
        loading={updateUserOrganizationLinkResult.loading}
        onChange={updateDigestPreference}
        modalHeader="Sponsor Digest Settings"
        formLabel="User Digest Preference"
      />
    </HStack>
  );
};
